import { useState } from 'react';
import { Message } from '../types';
import { languageAPI } from '../api/client';
import { AudioPlayer } from './AudioPlayer';

interface MessageBubbleProps {
  message: Message;
  userLanguage: string;
  contactLanguage: string | null;
}

export function MessageBubble({ message, userLanguage, contactLanguage }: MessageBubbleProps) {
  const [translatedText, setTranslatedText] = useState<string | null>(message.translated_text);
  const [showTranslation, setShowTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  const isSent = message.direction === 'sent';
  const isVoice = message.message_type === 'voice';
  const hasLanguageMismatch = contactLanguage && contactLanguage !== userLanguage;

  const handleTranslate = async () => {
    if (translatedText) {
      setShowTranslation(!showTranslation);
      return;
    }
    setTranslating(true);
    try {
      const result = await languageAPI.translateMessage(message.id, userLanguage);
      setTranslatedText(result.translated_text);
      setShowTranslation(true);
    } catch {
      // silently fail — user can retry
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className={`flex ${isSent ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`flex flex-col max-w-[80%] ${isSent ? 'items-end' : 'items-start'}`}>
        {/* Main bubble */}
        <div className={`px-3.5 py-2.5 rounded-xl text-sm ${
          isSent
            ? 'bg-accent/20 rounded-br-sm'
            : 'bg-ink/5 rounded-bl-sm'
        }`}>
          {/* Voice message: audio player + transcript */}
          {isVoice && message.media_path && (
            <div className="mb-1.5">
              <AudioPlayer mediaPath={message.media_path} />
            </div>
          )}

          {/* Voice transcript */}
          {isVoice && message.transcript ? (
            <p className="text-ink/60 italic text-xs">{message.transcript}</p>
          ) : isVoice && message.transcription_status === 'pending' ? (
            <p className="text-ink/40 text-xs">Transcribing...</p>
          ) : isVoice && message.transcription_status === 'failed' ? (
            <p className="text-ink/40 text-xs">Transcript unavailable</p>
          ) : null}

          {/* Text message body */}
          {!isVoice && <p>{message.body}</p>}
        </div>

        {/* Translation area */}
        {showTranslation && translatedText && (
          <div className={`px-3.5 py-2 rounded-b-xl text-sm bg-accent/5 border-t border-accent/10 ${
            isSent ? 'rounded-br-sm' : 'rounded-bl-sm'
          }`}>
            <p className="text-accent/80">{translatedText}</p>
            <span className="text-[10px] text-ink/30 mt-0.5 block">
              Translated from {contactLanguage || 'original'}
            </span>
          </div>
        )}

        {/* Outbound translation label */}
        {isSent && message.was_translated && (
          <button
            onClick={() => setShowOriginal(!showOriginal)}
            className="text-[10px] text-ink/30 mt-0.5 text-right block w-full"
          >
            Translated from {userLanguage}
          </button>
        )}

        {/* Show original for outbound translated messages */}
        {showOriginal && message.original_text && (
          <div className="px-3 py-1.5 mt-0.5 rounded-lg bg-ink/3 text-xs text-ink/50">
            {message.original_text}
          </div>
        )}

        {/* Tap to translate (inbound only, with language mismatch) */}
        {!isSent && hasLanguageMismatch && (
          <button
            onClick={handleTranslate}
            className="text-[11px] text-accent/50 mt-1 hover:text-accent transition-colors"
            disabled={translating}
          >
            {translating ? 'Translating...' : showTranslation ? 'Hide translation' : 'Translate'}
          </button>
        )}

        {/* Timestamp */}
        <span className={`text-[10px] text-ink/25 mt-0.5 block ${isSent ? 'text-right' : ''}`}>
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
}
